import QRCode from 'qrcode';
import api from '../services/api';

/**
 * Spool label printing shared by the QR label window and the weighed big-spool window:
 * label size from the printer settings, QR image and the printable label document.
 */

// Agreed spool label size (mm) — used when no size is set in the printer settings.
export const SPOOL_LABEL_WIDTH_MM = 60;
export const SPOOL_LABEL_HEIGHT_MM = 40;

// The label layout was designed for 76 x 54 mm; every dimension scales from it.
const LAYOUT_WIDTH_MM = 76;
const LAYOUT_HEIGHT_MM = 54;
export const MIN_LABEL_MM = 20;
export const MAX_LABEL_MM = 300;

export interface LabelSizeMm {
  width: number;
  height: number;
}

// Label size set by the admin in the printer settings; falls back to the agreed default.
export const fetchLabelSize = async (): Promise<LabelSizeMm> => {
  try {
    const res = await api.get('/packaging/printer-settings');
    const width = Number(res.data.label_width_mm);
    const height = Number(res.data.label_height_mm);
    if (width >= MIN_LABEL_MM && width <= MAX_LABEL_MM && height >= MIN_LABEL_MM && height <= MAX_LABEL_MM) {
      return { width, height };
    }
  } catch {
    // settings unavailable — print at the default size
  }
  return { width: SPOOL_LABEL_WIDTH_MM, height: SPOOL_LABEL_HEIGHT_MM };
};

export const parseLabelMm = (value: string): number | null => {
  const n = parseFloat(value.replace(',', '.'));
  return Number.isFinite(n) && n >= MIN_LABEL_MM && n <= MAX_LABEL_MM ? n : null;
};

/** High-resolution QR image for the printed label. */
export const qrDataUrlFor = (payload: string): Promise<string> =>
  QRCode.toDataURL(payload, {
    width: 600,
    // Quiet zone 2 modules — przy druku 34 mm skaner potrzebuje wyraźnego marginesu.
    margin: 2,
    color: { dark: '#000000', light: '#ffffff' },
  });

/** On-screen QR preview. */
export const drawQrPreview = (canvas: HTMLCanvasElement, payload: string): void => {
  QRCode.toCanvas(canvas, payload, {
    width: 220,
    margin: 2,
    color: { dark: '#000000', light: '#ffffff' },
  });
};

export interface LabelRow {
  label: string;
  value: string | null | undefined;
}

const esc = (s: string | null | undefined) =>
  String(s ?? '-').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));

/**
 * Writes the label (QR on the left, rows on the right) into an already opened window and
 * prints it. The window has to be opened synchronously in the click handler — after an await
 * the browser treats it as a popup without a user gesture and blocks it.
 */
export const writeLabelAndPrint = (
  printWindow: Window,
  { title, qrDataUrl, rows, widthMm, heightMm }: { title: string; qrDataUrl: string; rows: LabelRow[]; widthMm: number; heightMm: number },
): void => {
  const w = widthMm;
  const h = heightMm;
  const k = Math.min(w / LAYOUT_WIDTH_MM, h / LAYOUT_HEIGHT_MM);
  const mm = (v: number) => `${(v * k).toFixed(2)}mm`;
  const pad = 1.8 * k;
  const qrSize = Math.min(h - 2 * pad, 30.6 * (w / LAYOUT_WIDTH_MM));
  const rowsHtml = rows
    .map((r) => `<tr><td class="lbl">${esc(r.label)}</td><td class="val">${esc(r.value)}</td></tr>`)
    .join('\n    ');

  printWindow.document.write(`<!DOCTYPE html>
<html><head><title>${esc(title)}</title>
<style>
/* Etykieta ${w} x ${h} mm — QR po lewej, dane po prawej. */
@page{size:${w}mm ${h}mm;margin:0}
*{box-sizing:border-box;margin:0;padding:0}
html,body{width:${w}mm;height:${h}mm;font-family:Arial,Helvetica,sans-serif;color:#000;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.label{width:${w}mm;height:${h}mm;padding:${mm(1.8)};display:flex;align-items:center;gap:${mm(1.8)};overflow:hidden}
.qr{width:${qrSize.toFixed(2)}mm;height:${qrSize.toFixed(2)}mm;display:block;flex:0 0 ${qrSize.toFixed(2)}mm}
.info{flex:1 1 auto;border-collapse:collapse;table-layout:fixed;width:100%}
.info td{font-size:${mm(2.43)};line-height:1.15;padding:${mm(0.63)} ${mm(0.9)};border-bottom:${mm(0.18)} solid #ddd}
.info td.lbl{color:#555;font-weight:700;width:40%;white-space:nowrap}
.info td.val{font-weight:900;text-align:right;word-break:break-word}
</style>
</head><body>
<div class="label">
  <img class="qr" src="${qrDataUrl}" />
  <table class="info">
    ${rowsHtml}
  </table>
</div>
<script>window.onload=function(){setTimeout(function(){window.print();window.onafterprint=function(){window.close()}},150)}<\/script>
</body></html>`);
  printWindow.document.close();
};

/**
 * Spool weight as typed: digits and one decimal comma, up to 3 decimals — a dot is turned into a
 * comma, anything else is ignored (null), so "10.9" can never end up as 109 or an empty weight.
 */
export const sanitizeWeight = (raw: string): string | null => {
  const value = raw.replace(/\./g, ',').replace(/\s/g, '');
  return /^\d{0,4}(,\d{0,3})?$/.test(value) ? value : null;
};

/** Weight for the API, which expects a dot. */
export const apiWeight = (value: string): string => value.trim().replace(',', '.');
